/**
 * Israeli national ID (ת.ז) and company ID (ח.פ) validation and generation.
 *
 * Single source of truth for the Luhn/mod-10 checksum shared by both ID types —
 * consumed by both frontend and backend so the two never drift apart.
 */

export type NationalIdError = 'invalid_format' | 'invalid_checksum';
export type CompanyIdError = 'invalid_format' | 'invalid_leading_digit' | 'invalid_checksum';

/**
 * Luhn/mod-10 weighted digit sum shared by Israeli national IDs and company IDs —
 * both use the same 9-digit checksum algorithm, doubling alternate digits from the right.
 * Tolerates shorter input — `computeCheckDigit` relies on this to sum just the first 8 digits.
 */
function israeliChecksumSum(id: string): number {
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const char = id[i];
    if (!char) continue;
    let digit = parseInt(char, 10);
    if (i % 2 !== 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum;
}

/**
 * Solves for the 9th (check) digit given the first 8 — the check digit sits at an
 * even position (index 8) so it's never doubled, making this a direct solve.
 */
function computeCheckDigit(leadingDigits: readonly number[]): number {
  const partialSum = israeliChecksumSum(leadingDigits.join(''));
  return (10 - (partialSum % 10)) % 10;
}

function randomDigit(): number {
  return Math.floor(Math.random() * 10);
}

/**
 * Validates an Israeli national ID: exactly 9 digits + Luhn checksum.
 * Returns `null` when valid, otherwise the specific error code.
 */
export function validateNationalId(id: string): NationalIdError | null {
  if (!/^\d{9}$/.test(id)) return 'invalid_format';
  if (israeliChecksumSum(id) % 10 !== 0) return 'invalid_checksum';
  return null;
}

/**
 * Validates an Israeli company ID (ח.פ): 9 digits, must start with 5, same Luhn checksum
 * as national IDs. Returns `null` when valid, otherwise the specific error code.
 */
export function validateCompanyId(id: string): CompanyIdError | null {
  if (!/^\d{9}$/.test(id)) return 'invalid_format';
  if (id[0] !== '5') return 'invalid_leading_digit';
  if (israeliChecksumSum(id) % 10 !== 0) return 'invalid_checksum';
  return null;
}

/**
 * Validates an "entity ID" that may be either a company ID or a personal national ID
 * (e.g. a payer that can be a business or an individual).
 */
export function isValidEntityId(id: string): boolean {
  return validateCompanyId(id) === null || validateNationalId(id) === null;
}

function generateId(leadingDigits: number[]): string {
  return [...leadingDigits, computeCheckDigit(leadingDigits)].join('');
}

/**
 * Generates a random, always-valid Israeli national ID — 8 random digits plus a
 * computed check digit, guaranteed to pass `validateNationalId`.
 */
export function generateNationalId(): string {
  return generateId(Array.from({length: 8}, randomDigit));
}

/**
 * Generates a random, always-valid Israeli company ID — leading digit 5, 7 random
 * digits, and a computed check digit, guaranteed to pass `validateCompanyId`.
 */
export function generateCompanyId(): string {
  return generateId([5, ...Array.from({length: 7}, randomDigit)]);
}
